import { Chat, ChatMessage, LoadingAnimation, Prompt } from '../Types';
import {
    ContentStreamMessage,
    ContentStreamState,
    CostUpdateMessage,
    SocketMessageTypes,
    TitleListUpdateMessage,
    TokenCountUpdateMessage,
} from '../Types/SocketMessage';
import { DLPState } from '../Types/Enums';
import {
    addMessage,
    setChatMessages,
    setChat as setGlobalChat,
    setIsStopable,
    setMessagesLoaded,
    updateMessageMetadata,
    updateMessage,
    setChatLoaded,
    setIsNewChat,
    setIsSending,
    setUpdateChat,
} from '../Store/features/chat/chatSlice';
import {
    setNavigationImage,
    setNavigationSubTitle,
    setNavigationTitle,
} from '../Store/features/navigationHeader/navigationHeaderSlice';
import { SocketContext } from '../SocketContext';
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { UserSettings } from '../Types';
import useResizableContainer from './useResizableContainer';
import { chatService } from '../Services/chatService';
import { setMessageSource } from '../Store/features/messageSource/messageSourceSlice';
import usePromptState from './state/usePromptState';
import { getBreakpoint } from '../utils/breakpoint';
import { selectChatMessagesById } from '../Store/selectors/chatSelectors';

type ChatProps = {
    chatId?: string;
    promptPreview?: boolean;
    assistantIdentifier?: string;
    initMessage?: string;
};

const useChat = ({
    chatId: propChatId,
    assistantIdentifier: propAssistantIdentifier,
    initMessage: propInitMessage,
}: ChatProps) => {
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const currentURL = useAppSelector((state: RootState) => state.environment.currentURL);
    const dispatch = useAppDispatch();

    const { chatId: paramChatId } = useParams<{ chatId: string }>();
    const chatId = useMemo(() => {
        const result = propChatId || (paramChatId as string);
        return result;
    }, [propChatId, paramChatId]);

    const { initMessage: paramInitMessage } = useParams<{
        assistantIdentifier: string;
        initMessage: string;
    }>() as { initMessage: string };
    const initMessage =
        propInitMessage || paramInitMessage
            ? decodeURI((propInitMessage || paramInitMessage).replace(/\+/g, '%20'))
            : undefined;
    const { socket, addMessageHandler } = useContext(SocketContext);

    const [keyboardVisible, setKeyboardVisible] = useState<boolean>(false);
    const [showSplitButton, setShowSplitButton] = useState<boolean>(false);
    const [chatWidth, setChatWidth] = useState<number>(windowWidth);

    const isDragActive = useAppSelector((state: RootState) => state.environment.isDragActive);
    const dragZoneRootProps = useAppSelector((state: RootState) => state.environment.dragZoneRootProps);
    const dragZoneInputProps = useAppSelector((state: RootState) => state.environment.dragZoneInputProps);

    const chatLoaded = useAppSelector((state: RootState) => state.chats.chatLoaded);
    const updateChat = useAppSelector((state: RootState) => state.chats.updateChat);
    const websocketError = useAppSelector((state: RootState) => state.chats.websocketError);
    const chat = useAppSelector((state: RootState) => state.chats.chat as Chat);
    const chatMessages = useAppSelector((state: RootState) => selectChatMessagesById(state, chatId)) as {
        [key: string]: any;
    };
    const chatContainerStyles = useAppSelector((state: RootState) => state.chats.chatContainerStyles);

    const chatlistOpen = useAppSelector((state: RootState) => state.chatlist.chatlistOpen);
    const messageSourceIdentifier = useAppSelector((state: RootState) => state.messageSource.messageIdentifier);

    const { prompts, selectedPrompt, setSelectedPrompt, getPromptValueById, combinedPrompts } = usePromptState();
    const [showInput, setShowInput] = useState<boolean>(true);

    const [dlpState, setDlpState] = useState<DLPState>(DLPState.dlp_optional);

    const { assistantIdentifier: paramAssistantIdentifier } = useParams<{ assistantIdentifier: string }>();
    const assistantIdentifier = propAssistantIdentifier || paramAssistantIdentifier;

    const [loadingAnimation, setLoadingAnimation] = useState<LoadingAnimation>({
        state: 'closed',
        content: '',
        percentage: 100,
        icon: 'Check',
    });

    const { resizeContainerRef, handleMouseDown } = useResizableContainer({
        chatId,
        chatWidth,
    });

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const resizerRef = useRef<HTMLDivElement | null>(null);
    const isFirstScroll = useRef<boolean>(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const getPromptValueByIdRef = useRef(getPromptValueById);
    const currentChatMessagesRef = useRef(chatMessages);

    useEffect(() => {
        getPromptValueByIdRef.current = getPromptValueById;
    }, [getPromptValueById]);

    useEffect(() => {
        currentChatMessagesRef.current = chatMessages;
    }, [chatMessages]);

    const promptPreview = !currentURL.includes('chat') && !currentURL.includes('start') && !currentURL.endsWith('/');

    const updateChatDetails = useCallback(
        (updates: Partial<Chat>) => {
            dispatch(setGlobalChat({ ...updates }));
        },
        [dispatch],
    );

    useEffect(() => {
        setShowInput(prompts.find((prompt: Prompt) => prompt.identifier === selectedPrompt?.identifier) !== undefined);
    }, [prompts, selectedPrompt]);

    const chatloadingRef = useRef(false);

    useEffect(() => {
        if (chatId !== undefined) {
            if (chatloadingRef.current) return;

            chatloadingRef.current = true;
            dispatch(setChatLoaded(false));
            dispatch(setMessagesLoaded(false));

            const fetchChat = async () => {
                try {
                    const response = await chatService.getChat(chatId);
                    let tempChats: Record<string, ChatMessage> = {};
                    response.data.messages.forEach((message: ChatMessage) => {
                        tempChats[message.identifier] = {
                            identifier: message.identifier,
                            role: message.role,
                            content: message.content,
                            images: message.images,
                            files: message.files,
                            sources: message.sources || [],
                            contentChanged: message.contentChanged,
                            originalContent: message.originalContent,
                        };
                    });

                    if (Object.keys(currentChatMessagesRef.current ?? {}).length) {
                        const key = Object.keys(currentChatMessagesRef.current).at(-1) as string;
                        tempChats[key] = currentChatMessagesRef.current[key];
                    }

                    const selectedPromptValue = getPromptValueByIdRef.current(response.data.prompt);

                    dispatch(setGlobalChat(response.data));
                    dispatch(setChatMessages({ chatIdentifier: chatId, payload: tempChats }));
                    if (!promptPreview) {
                        if (response.data.title !== '') dispatch(setNavigationTitle(response.data.title));
                        dispatch(setNavigationSubTitle(response.data.prompt_name));
                        dispatch(setNavigationImage(response.data.prompt_image));
                    }
                    setSelectedPrompt(selectedPromptValue);
                    dispatch(setMessagesLoaded(true));
                    dispatch(setChatLoaded(true));

                    chatloadingRef.current = false;
                } catch (error) {
                    console.error('Error fetching chat:', error);
                    chatloadingRef.current = false;
                }
            };

            fetchChat();
            dispatch(setIsNewChat(false));
        } else {
            chatloadingRef.current = false;
            updateChatDetails({ identifier: '', title: '', messages: [] });
            dispatch(setChatLoaded(false));
        }
        isFirstScroll.current = true;
        return () => {
            dispatch(
                setMessageSource({
                    messageIdentifier: '',
                    content: [{ title: '', description: '', url: '' }],
                }),
            );
        };
    }, [chatId, dispatch, updateChatDetails, promptPreview, getPromptValueByIdRef, setSelectedPrompt]);

    useEffect(() => {
        if (selectedPrompt && chatId === undefined) {
            updateChatDetails({ prompt_examples: selectedPrompt.examples });
            setDlpState(selectedPrompt.combinedDlp);
        }
    }, [selectedPrompt, chatId, updateChatDetails]);

    useEffect(() => {
        if (assistantIdentifier && chatId === undefined) {
            const allPrompts = prompts.length > 0 ? prompts : (combinedPrompts || []);
            if (allPrompts.length > 0) {
                const prompt = allPrompts.find((p: Prompt) => p.identifier === assistantIdentifier);
                if (prompt && prompt.identifier === assistantIdentifier) {
                    const currentIdentifier = selectedPrompt?.identifier;
                    if (currentIdentifier !== assistantIdentifier) {
                        setSelectedPrompt(prompt);
                    }
                }
            }
        }
    }, [assistantIdentifier, chatId, prompts, combinedPrompts, setSelectedPrompt, selectedPrompt]);

    useEffect(() => {
        chatloadingRef.current = false;
    }, [chatId]);

    useEffect(() => {
        if (chatId === undefined) return;

        const initialViewportHeight = window.visualViewport?.height ?? 0;
        const handleResize = () => {
            if (window.visualViewport && window.visualViewport.height < initialViewportHeight) {
                setKeyboardVisible(true);
            } else {
                setKeyboardVisible(false);
            }
        };
        window.visualViewport?.addEventListener('resize', handleResize);
        return () => {
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [chatId]);

    useEffect(() => {
        if (chatId === undefined) return;
        let chatWidth;

        if (chatlistOpen && windowWidth > getBreakpoint('lg')) {
            if (messageSourceIdentifier !== '') {
                chatWidth = windowWidth - 700;
            } else {
                chatWidth = windowWidth - 300;
            }
        } else {
            if (messageSourceIdentifier !== '') {
                chatWidth = windowWidth;
            } else {
                chatWidth = windowWidth;
            }
        }

        setChatWidth(chatWidth);
    }, [chatlistOpen, windowWidth, chatId, messageSourceIdentifier]);

    const chatMessagesRef = useRef(chat.messages);

    useEffect(() => {
        chatMessagesRef.current = chat.messages;
    }, [chat.messages]);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.titleUpdate, (data: TitleListUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                updateChatDetails({ title: data.title });
                window.location.pathname.includes('chat') && dispatch(setNavigationTitle(data.title));
            }
        });

        addMessageHandler(SocketMessageTypes.tokenCountUpdate, (data: TokenCountUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                // setTokenCount(data.count);
            }
        });

        addMessageHandler(SocketMessageTypes.costUpdate, (data: CostUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                // setCosts(data.value);
            }
        });

        addMessageHandler('addSourcesAndImages', (data) => {
            const currentMessages = currentChatMessagesRef.current ?? {};
            if (Object.keys(currentMessages).includes(data.messageIdentifier) && data.messageIdentifier !== undefined) {
                dispatch(
                    updateMessage({
                        chatIdentifier: data.chatIdentifier,
                        ...currentMessages[data.messageIdentifier],
                        sources: data.sources,
                        images: data.images,
                    }),
                );
            }
        });

        addMessageHandler(SocketMessageTypes.contentStream, (data: ContentStreamMessage) => {
            if (data.chatIdentifier && data.messageIdentifier) {
                switch (data.state) {
                    case ContentStreamState.complete:
                        dispatch(
                            addMessage({
                                chatIdentifier: data.chatIdentifier,
                                identifier: data.messageIdentifier,
                                role: data.role,
                                content: data.content,
                                images: data.images,
                                files: data.files,
                                sources: data.sources || [],
                                contentChanged: data.contentChanged,
                                originalContent: data.originalContent,
                            }),
                        );
                        break;
                    case ContentStreamState.started:
                        dispatch(setIsSending(true));
                        dispatch(setIsStopable(true));
                        dispatch(
                            addMessage({
                                chatIdentifier: data.chatIdentifier,
                                identifier: data.messageIdentifier,
                                role: data.role,
                                content: '',
                                images: data.images,
                                files: data.files,
                                sources: data.sources || [],
                                contentChanged: data.contentChanged,
                                originalContent: data.originalContent,
                            }),
                        );
                        dispatch(
                            updateMessageMetadata({
                                chatIdentifier: data.chatIdentifier,
                                identifier: data.messageIdentifier,
                                isSending: true,
                                isStopable: true,
                            }),
                        );

                        if (data.chatIdentifier === chatId) {
                            setLoadingAnimation({
                                state: 'update',
                                content: i18n.translate({
                                    id: 'openaiassistant.working',
                                    sourceName: 'LoadingAnimation',
                                }),
                                percentage: 0,
                                icon: 'Spinner',
                            });
                        }
                        break;
                    case ContentStreamState.finished:
                        dispatch(setIsSending(false));
                        dispatch(setIsStopable(false));
                        dispatch(
                            updateMessageMetadata({
                                chatIdentifier: data.chatIdentifier,
                                identifier: data.messageIdentifier,
                                isSending: false,
                                isStopable: false,
                            }),
                        );
                        break;
                    default:
                        dispatch(setIsSending(true));
                        dispatch(setIsStopable(true));

                        const messagePayload = {
                            chatIdentifier: data.chatIdentifier,
                            identifier: data.messageIdentifier,
                            role: data.role,
                            content: data.content,
                            ...(data.images && { images: data.images }),
                            ...(data.files && { files: data.files }),
                            ...(data.sources && { sources: data.sources }),
                            contentChanged: data.contentChanged,
                            originalContent: data.originalContent,
                        };

                        dispatch(updateMessage(messagePayload));
                        dispatch(
                            updateMessageMetadata({
                                chatIdentifier: data.chatIdentifier,
                                identifier: data.messageIdentifier,
                                isSending: true,
                                isStopable: true,
                            }),
                        );
                        break;
                }
            }
        });

        addMessageHandler(SocketMessageTypes.loadingAnimation, (data: ContentStreamMessage) => {
            if (data.chatIdentifier === chatId) {
                if (data.state === 'closed') {
                    data.content = '';
                    data.value = 100;
                    data.icon = 'Check';
                    data.image = '';
                }

                setLoadingAnimation((prev) => {
                    if (
                        prev.state === data.state &&
                        prev.content === data.content &&
                        prev.percentage === (data.value ?? 100) &&
                        prev.icon === (data.icon ?? 'Check') &&
                        prev.image === (data.image ?? '')
                    ) {
                        return prev;
                    }
                    return {
                        ...prev,
                        state: data.state ?? 'closed',
                        content: data.content ?? '',
                        percentage: data.value ?? 100,
                        icon: data.icon ?? 'Check',
                        image: data.image ?? '',
                    };
                });
            }
        });
    }, [chatId, dispatch, updateChatDetails, addMessageHandler, i18n]);

    const handleMouseMove = useCallback(() => {
        if (chatId === undefined) return;

        setShowSplitButton(true);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setShowSplitButton(false);
        }, 1000);
    }, [chatId]);

    useEffect(() => {
        setLoadingAnimation({
            state: 'closed',
            content: '',
            percentage: 100,
            icon: 'Check',
        });
    }, [chatId]);

    useEffect(() => {
        if (chatId === undefined) return;

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [chatId, showSplitButton, setShowSplitButton, handleMouseMove]);

    const sliceMessages = useCallback(
        (messageIdentifier: string) => {
            if (!chatId) return;
            const messagesArray = Object.values(chatMessages);
            const index = messagesArray.findIndex((message: ChatMessage) => message.identifier === messageIdentifier);

            if (index === -1) return;

            const newMessages = messagesArray.slice(0, index);
            const newMessagesObject = Object.fromEntries(newMessages.map((msg: ChatMessage) => [msg.identifier, msg]));

            dispatch(setChatMessages({ chatIdentifier: chatId, payload: newMessagesObject }));
        },
        [chatId, chatMessages, dispatch],
    );

    useEffect(() => {
        if (chatId === undefined) return;
        if (userSettings.isLoggedIn && chatId !== undefined && !promptPreview && updateChat) {
            const data = new URLSearchParams();
            data.append('dlpActive', chat.dlp_active ? 'true' : 'false');
            chatService.updateDLP(chatId, data).then((response) => {
                dispatch(setUpdateChat(false));
                updateChatDetails({ dlp_active: response.data.dlp_active });
            });
        }
    }, [
        updateChat,
        chatId,
        userSettings.isLoggedIn,
        chat.dlp_active,
        dlpState,
        promptPreview,
        dispatch,
        updateChatDetails,
        chat.dlp,
    ]);

    // const ChatActions = useMemo(() => ({}), []);

    return {
        chat,
        i18n,
        dlpState,
        sliceMessages,
        socket,
        messagesEndRef,
        loadingAnimation,
        promptPreview,
        chatContainerStyles,
        websocketError,
        chatId,
        keyboardVisible,
        dragZoneRootProps,
        dragZoneInputProps,
        demo,
        showSplitButton,
        resizerRef,
        isDragActive,
        chatLoaded,
        handleMouseDown,
        resizeContainerRef,
        chatMessages,
        initMessage,
        showInput,
    };
};

export default useChat;
